/*
  Отправить СМС одному должнику - окно из карточки счёта (06.10.2026).

  Это волна на один счёт: тот же разбор и та же отправка, что у рассылки по
  списку, с теми же правилами частоты, подписью юриста и ценой. Своего пути
  отправки у карточки нет намеренно - второй путь разошёлся бы с первым.

  Текст сообщения здесь не показывается (решение владельца 08.09.2026): слова
  подписывает юрист Арбитра ЖКХ, оператор выбирает формулировку по названию.
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { Loading, Notice } from './Common.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

export default function SmsDialog({ accountId, title, onClose }) {
  const [templates, setTemplates] = useState(null)
  const [chosen, setChosen] = useState('')
  const [plan, setPlan] = useState(null)
  const [state, setState] = useState({ busy: false, sent: null, error: null })

  useEffect(() => {
    api
      .messageTemplates()
      .then((library) => {
        setTemplates(Object.fromEntries(library.templates.map((row) => [row.key, row.title])))
        setChosen(library.templates[0]?.key ?? '')
      })
      .catch((error) => setState((now) => ({ ...now, error })))
  }, [])

  useEffect(() => {
    if (!chosen) return undefined
    // Разбор для прежней формулировки, пришедший после смены выбора, выбрасывается
    // (аудит 26.10.2026, причина 5): иначе «Отправить» ушёл бы с разбором другого текста.
    let current = true
    setPlan(null)
    api
      .planMessageWave({ template_key: chosen, account_ids: [accountId] })
      .then((answer) => {
        if (current) setPlan(answer)
      })
      .catch((error) => {
        if (current) setState((now) => ({ ...now, error }))
      })
    return () => {
      current = false
    }
  }, [chosen, accountId])

  async function send() {
    setState({ busy: true, sent: null, error: null })
    try {
      const sent = await api.sendMessageWave({
        template_key: chosen,
        account_ids: [accountId],
      })
      setState({ busy: false, sent, error: null })
    } catch (error) {
      setState({ busy: false, sent: null, error })
    }
  }

  const sent = state.sent
  return (
    <Modal title={`Отправить СМС: ${title}`} onClose={onClose} busy={state.busy}>
      <div className="stack">
        {templates ? (
          <div className="field">
            <span className="field__label" id="sms-one-template">
              Формулировка
            </span>
            <Dropdown
              value={chosen}
              onChange={setChosen}
              options={templates}
              labelledBy="sms-one-template"
            />
          </div>
        ) : null}
        {chosen && !plan && !state.error ? <Loading what="Считаем сообщение" /> : null}
        {plan ? <OnePlan plan={plan} /> : null}
        {sent?.refused ? (
          <Notice kind="warn">
            <div className="notice__what">Сообщение не отправлено.</div>
            <div className="notice__do">{sent.refused}</div>
          </Notice>
        ) : null}
        {sent && !sent.refused ? (
          <Notice kind={sent.accepted ? 'ok' : 'warn'}>
            {sent.accepted
              ? 'Сообщение передано оператору связи. Оно появится в «Последних обращениях».'
              : 'Оператор связи сообщение не принял - причина в «Последних обращениях».'}
          </Notice>
        ) : null}
        <Notice error={state.error} />
        <div className="row">
          {!sent ? (
            <button
              type="button"
              className="button"
              disabled={state.busy || !plan?.can_send}
              onClick={send}
            >
              {state.busy ? 'Отправляем…' : 'Отправить'}
            </button>
          ) : null}
          <button type="button" className="button button--quiet" onClick={onClose}>
            {sent ? 'Закрыть' : 'Отмена'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function OnePlan({ plan }) {
  const refused = plan.skipped[0]
  return (
    <div className="stack">
      {refused ? (
        <Notice kind="warn">
          <div className="notice__what">Сейчас писать нельзя.</div>
          <div className="notice__do">{refused.explanation}</div>
        </Notice>
      ) : (
        <div className="muted">{plan.cost_explanation}</div>
      )}
      {plan.length_notice ? <div className="muted">{plan.length_notice}</div> : null}
      {plan.channel_blocked ? (
        <Notice kind="warn">
          <div className="notice__what">Отправить нечем.</div>
          <div className="notice__do">{plan.channel_blocked}</div>
        </Notice>
      ) : null}
      {plan.release_blocked ? (
        <Notice kind="warn">
          <div className="notice__what">Эти слова отправлять нельзя.</div>
          <div className="notice__do">{plan.release_blocked}</div>
        </Notice>
      ) : null}
    </div>
  )
}
